import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

interface RpcResult {
  success: boolean
  error?: string
  from_status?: string
  to_status?: string
}

/**
 * Calls the Supabase RPC function to validate a user story.
 * Transitions from 'pending_validation' to 'validated'.
 */
async function validateUserStory(userStoryId: string): Promise<void> {
  const { data, error } = await supabase.rpc('validate_user_story', {
    p_user_story_id: userStoryId,
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as RpcResult
  if (!result.success) {
    throw new Error(result.error || 'Failed to validate user story')
  }
}

export function useValidateUserStory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: validateUserStory,
    onSuccess: () => {
      // Invalidate user story queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['userStory'] })
      queryClient.invalidateQueries({ queryKey: ['userStories'] })
      // Also invalidate features (for US count/status updates)
      queryClient.invalidateQueries({ queryKey: ['feature'] })
      queryClient.invalidateQueries({ queryKey: ['features'] })
    },
  })
}
